import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './MonthYearPicker.css';

interface MonthYearPickerProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  year: number;
  month: number; // 0-11
  locale: string;
  onSelect: (year: number, month: number) => void;
  onClose: () => void;
}

const MONTH_REF_DATES = Array.from({ length: 12 }, (_, m) => new Date(2000, m, 1));

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  isOpen,
  anchorEl,
  year,
  month,
  locale,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [displayYear, setDisplayYear] = useState(year);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isOpen) setDisplayYear(year);
  }, [isOpen, year]);

  useLayoutEffect(() => {
    if (!isOpen || !anchorEl) {
      setPosition(null);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const popoverWidth = popoverRef.current?.offsetWidth || 260;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - popoverWidth - 8
    );
    setPosition({ top: rect.bottom + 6, left });
  }, [isOpen, anchorEl]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };
    const handleScroll = () => onClose();
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, anchorEl, onClose]);

  if (!isOpen || !position) return null;

  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      className="myp-popover"
      role="dialog"
      aria-label={t('calendar.jumpToDate')}
      style={{ top: position.top, left: position.left }}
    >
      <div className="myp-year-row">
        <button
          type="button"
          className="myp-year-nav"
          aria-label={t('common.previous')}
          onClick={() => setDisplayYear(y => y - 1)}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="myp-year-label">{displayYear}</span>
        <button
          type="button"
          className="myp-year-nav"
          aria-label={t('common.next')}
          onClick={() => setDisplayYear(y => y + 1)}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="myp-grid">
        {MONTH_REF_DATES.map((refDate, m) => {
          const selected = displayYear === year && m === month;
          return (
            <button
              key={m}
              type="button"
              className={`myp-month-btn ${selected ? 'selected' : ''}`}
              aria-pressed={selected}
              onClick={() => onSelect(displayYear, m)}
            >
              {new Intl.DateTimeFormat(locale, { month: 'short' }).format(refDate)}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
};

export default MonthYearPicker;
